import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../hooks/useInView';
import './Home.css';

/* ─── Scroll reveal ─── */
function Reveal({ children, className = '', dir = '' }) {
  const [ref, inView] = useInView();
  const cls = dir === 'l' ? 'reveal-l' : dir === 'r' ? 'reveal-r' : 'reveal';
  return (
    <div ref={ref} className={`${cls} ${inView ? 'revealed' : ''} ${className}`}>
      {children}
    </div>
  );
}

const TRUST = [
  { icon: '❄', label: 'Cold-Pressed & Unrefined' },
  { icon: '◎', label: 'No Additives / Clean Label' },
  { icon: '✦', label: 'Export-Ready Supply' },
  { icon: '≡', label: 'Consistent Batch Quality' },
];

const WHY = [
  { n: '01', title: 'Consistent Quality', body: 'Every batch meets the same specifications. Controlled extraction temperature and in-house quality checks ensure reliability across orders.' },
  { n: '02', title: 'Low-Temperature Extraction', body: 'Cold-pressed at controlled temperatures to preserve natural aroma, flavour compounds, and nutritional integrity.' },
  { n: '03', title: 'Reliable Production Capacity', body: 'Scalable monthly supply capability to support both trial orders and sustained distribution volumes.' },
  { n: '04', title: 'Flexible Packaging', body: 'Available in retail SKUs (250 ml – 1 L) and bulk formats. Custom private-label packaging available for qualified partners.' },
  { n: '05', title: 'Export-Focused Operations', body: 'Experienced in documentation, compliance, and logistics for UK, EU, and Middle East markets.' },
];

const APPS = [
  { label: 'Foodservice / HoReCa', desc: 'Restaurant-grade sesame oil for professional kitchens.' },
  { label: 'Retail Distribution', desc: 'Shelf-ready SKUs for grocery and specialty food retailers.' },
  { label: 'Ethnic & Specialty', desc: 'Authentic flavour profile for ethnic food markets globally.' },
];

export default function Home() {
  /* hero text animation on mount */
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="home">

      {/* ════ HERO ════ */}
      <section className="hero">
        <div className="hero__grain" />
        <div className="hero__lines" aria-hidden="true">
          {[...Array(6)].map((_,i) => <div key={i} className="hero__line" style={{ animationDelay:`${i*0.15}s` }} />)}
        </div>

        <div className="hero__inner">
          <div className="hero__left">
            <span className="hero__eyebrow">India · Cold-Pressed · Export-Ready</span>
            <h1 className="hero__h1">
              Cold-Pressed Oils<br/>
              <em>from India.</em>
            </h1>
            <p className="hero__sub2">Consistent. Natural. Export-Ready.</p>
            <p className="hero__body">
              Premium sesame oil crafted through controlled low-temperature extraction,
              ensuring natural aroma, purity, and reliable supply for global partners.
            </p>
            <div className="hero__ctas">
              <Link to="/contact" className="btn btn--gold">Request Product Details</Link>
              <Link to="/products" className="btn btn--ghost">View Products</Link>
            </div>
          </div>

          <div className="hero__right">
            {/* ── PRIMARY PRODUCT IMAGE ──────────────────────────────────────────
                This is the main sesame oil bottle image shown in the hero.
                ► Replace /images/sesame.jpeg with your preferred image.
                ► File must be placed in: frontend/public/images/
                ► Recommended: a clean product shot on white/neutral background
                ► Ideal size: 600×700px or similar portrait ratio
            ──────────────────────────────────────────────────────────────────── */}
            <div className="hero__img-frame">
              <img
                src="/images/logo.jpeg"
                alt="Nativeroots Cold-Pressed Sesame Oil"
                className="hero__img"
              />
              <div className="hero__img-badge hero__img-badge--tl">Cold-Pressed</div>
              <div className="hero__img-badge hero__img-badge--br">Export-Ready</div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="hero__strip">
          <div className="hero__marquee">
            {[...Array(4)].map((_, i) => (
              <span key={i} className="hero__marquee-track">
                Bulk Supply Available &nbsp;·&nbsp; Export Partnerships &nbsp;·&nbsp; UK · EU · Middle East &nbsp;·&nbsp; Clean Label &nbsp;·&nbsp; Cold-Pressed &nbsp;·&nbsp;
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ════ TRUST SIGNALS ════ */}
      <section className="trust">
        {TRUST.map((t, i) => (
          <Reveal key={i} className="trust__item" style={{ transitionDelay:`${i*0.1}s` }}>
            <span className="trust__icon">{t.icon}</span>
            <span className="trust__label">{t.label}</span>
          </Reveal>
        ))}
      </section>

      {/* ════ BOLD EXPORT LINE ════ */}
      <section className="export-line">
        <Reveal>
          <p className="export-line__text">
            Bulk supply and export partnerships available —<br/>
            <em>UK, EU &amp; Middle East markets.</em>
          </p>
        </Reveal>
      </section>

      {/* ════ ABOUT ════ */}
      <section className="about">
        <div className="about__inner">
          <Reveal dir="l" className="about__img-col">
            {/* ── ABOUT SECTION IMAGE ───────────────────────────────────────────
                Image shown beside the About Us text.
                ► Replace /images/image.png with your preferred image.
                ► Could be: your facility, oil bottles, production process, team
                ► File must be in: frontend/public/images/
            ──────────────────────────────────────────────────────────────────── */}
            <div className="about__img-wrap">
              <img src="/images/herooo.jpeg" alt="Nativeroots production" className="about__img" />
              <div className="about__img-tag">Est. Karnataka, India</div>
            </div>
          </Reveal>

          <Reveal dir="r" className="about__text-col">
            <span className="eyebrow">About Nativeroots</span>
            <h2 className="h2">Producer of Cold-Pressed Oils, India</h2>
            <p>
              Nativeroots is a producer of cold-pressed oils based in India, focused on delivering
              high-quality, unrefined oils for international markets. Our controlled extraction
              process preserves natural aroma and nutritional value while ensuring consistency
              across batches.
            </p>
            <p style={{marginTop:'1rem'}}>
              We are currently working with partners across the <strong>UK, EU, and Middle East</strong> markets,
              supporting both retail and bulk requirements.
            </p>
            <div className="about__meta">
              <div className="about__meta-item"><strong>S R Industries</strong><span>Manufacturer</span></div>
              <div className="about__meta-item"><strong>Tumkur, Karnataka</strong><span>Origin</span></div>
              <div className="about__meta-item"><strong>FSSAI</strong><span>Licensed</span></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════ PRIMARY PRODUCT ════ */}
      <section className="product-hero">
        <div className="product-hero__inner">
          <Reveal className="product-hero__left">
            <span className="eyebrow eyebrow--light">Primary Product</span>
            <h2 className="h2 h2--light">Cold-Pressed Sesame Oil</h2>
            <p className="product-hero__body">
              Cold-pressed and unrefined, our sesame oil delivers a clean, authentic flavour
              profile suited to both foodservice and retail channels. Consistent batch quality,
              natural aroma, and a clean ingredient label.
            </p>
            <div className="product-hero__skus">
              <span className="eyebrow eyebrow--light" style={{marginBottom:'.5rem',display:'block'}}>Available SKUs</span>
              {['250 ml', '500 ml — Hero SKU', '1 L', 'Bulk (on request)'].map(s => (
                <div key={s} className={`sku-row ${s.includes('Hero') ? 'sku-row--hero' : ''}`}>
                  <span className="sku-dot"/>
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="btn btn--gold" style={{marginTop:'1.75rem',display:'inline-flex'}}>
              Request Specifications →
            </Link>
          </Reveal>

          <Reveal dir="r" className="product-hero__right">
            {/* ── SESAME OIL PRODUCT IMAGE ──────────────────────────────────────
                Large product image in the "Primary Product" section.
                ► Replace /images/sesame.jpeg with a higher-res bottle shot.
                ► For best results: white/transparent background, portrait ratio
                ► File must be in: frontend/public/images/
            ──────────────────────────────────────────────────────────────────── */}
            <div className="product-hero__img-wrap">
              <img src="/images/sesame.jpeg" alt="Cold-Pressed Sesame Oil" className="product-hero__img" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════ OTHER OILS ════ */}
      <section className="other-oils section">
        <Reveal className="section__hd">
          <span className="eyebrow">Also Available</span>
          <h2 className="h2">Other Cold-Pressed Oils</h2>
          <p className="section__sub">Available based on sourcing and volume requirements.</p>
        </Reveal>
        <div className="other-oils__grid">
          {[
            {
              name: 'Groundnut Oil',
              note: 'High smoke point · Cooking-grade',
              /* ── GROUNDNUT IMAGE ──
                 Replace /images/nut.jpeg with your groundnut oil image.
                 File location: frontend/public/images/ */
              img: '/images/nut.jpeg',
            },
            {
              name: 'Coconut Oil',
              note: 'Unrefined · Virgin & Standard',
              /* ── COCONUT IMAGE ──
                 Replace /images/cocunut.jpeg with your coconut oil image.
                 File location: frontend/public/images/ */
              img: '/images/cocunut.jpeg',
            },
            {
              name: 'Castor Oil',
              note: 'Multi-purpose · Personal care',
              /* ── CASTOR IMAGE ──
                 Replace /images/castor.jpeg with your castor oil image.
                 File location: frontend/public/images/ */
              img: '/images/castor.jpeg',
            },
          ].map((oil, i) => (
            <Reveal key={oil.name} className="oil-card" style={{ transitionDelay:`${i*0.12}s` }}>
              <div className="oil-card__img-wrap">
                <img src={oil.img} alt={oil.name} loading="lazy" />
              </div>
              <div className="oil-card__body">
                <h3>{oil.name}</h3>
                <p>{oil.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="other-oils__note">
          <Link to="/products" className="btn btn--outline">View All Products →</Link>
        </Reveal>
      </section>

      {/* ════ WHY CHOOSE US ════ */}
      <section className="why section section--dark">
        <div className="why__inner">
          <Reveal className="why__hd">
            <span className="eyebrow eyebrow--light">Why Choose Us</span>
            <h2 className="h2 h2--light">The Nativeroots Standard</h2>
          </Reveal>
          <div className="why__grid">
            {WHY.map((w, i) => (
              <Reveal key={w.n} className="why__card" style={{ transitionDelay:`${i*0.1}s` }}>
                <span className="why__num">{w.n}</span>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════ APPLICATIONS ════ */}
      <section className="apps section">
        <Reveal className="section__hd">
          <span className="eyebrow">Applications</span>
          <h2 className="h2">Where Our Oil Is Used</h2>
        </Reveal>
        <div className="apps__grid">
          {APPS.map((a, i) => (
            <Reveal key={a.label} className="apps__card" style={{ transitionDelay:`${i*0.12}s` }}>
              <div className="apps__num">0{i+1}</div>
              <h3>{a.label}</h3>
              <p>{a.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ════ SUPPLY & CAPACITY ════ */}
      <section className="supply section section--cream">
        <Reveal className="supply__inner">
          <span className="eyebrow">Supply & Capacity</span>
          <h2 className="h2">Scalable for Your Requirements</h2>
          <div className="supply__grid">
            {[
              ['Scalable Production', 'Volume can be adjusted to meet growing demand from distributors and importers.'],
              ['Monthly Supply', 'Regular monthly dispatch capability to support sustained distribution.'],
              ['Bulk & Packaged', 'Both bulk and finished-packaged formats available to suit your supply chain.'],
            ].map(([t,d]) => (
              <div className="supply__item" key={t}>
                <div className="supply__tick">✓</div>
                <div><strong>{t}</strong><p>{d}</p></div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ════ CTA BANNER ════ */}
      <section className="cta-banner">
        <Reveal className="cta-banner__inner">
          <span className="eyebrow eyebrow--light">Work With Us</span>
          <h2 className="cta-banner__h2">Actively Seeking Global Partners</h2>
          <p className="cta-banner__body">
            We are exploring partnerships with importers, distributors, and foodservice suppliers.
            Request specifications, pricing, or samples.
          </p>
          <div className="cta-banner__btns">
            <Link to="/contact" className="btn btn--gold">Request Details</Link>
            <a href="https://wa.me/918861432099" className="btn btn--ghost-light" target="_blank" rel="noopener noreferrer">
              WhatsApp Us
            </a>
          </div>
        </Reveal>
      </section>

      {/* ════ FOOTER ════ */}
      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            {/* ── FOOTER LOGO ──
                Replace /images/logo.jpeg if you have a different logo.
                File location: frontend/public/images/ */}
            <img src="/images/logo11.jpeg" alt="Nativeroots" className="footer__logo" />
            <div>
              <div className="footer__name">Nativeroots</div>
              <div className="footer__sub">S R Industries, Tumkur, Karnataka</div>
            </div>
          </div>
          <nav className="footer__nav">
            <Link to="/">Home</Link>
            <Link to="/products">Products</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/contact">Export Enquiries</Link>
          </nav>
          <div className="footer__contact">
            <a href="tel:+918861432099">+91 88614 32099</a>
            <div>
              <a href="mailto:rakeshcsmail@gmail.com">rakeshcsmail@gmail.com</a>
              <br />
              <a href="mailto:rakesh@nativerootsheritage.co.in">rakesh@nativerootsheritage.co.in</a>
            </div>
            <a href="https://www.instagram.com/nativeroots_heritage" target="_blank" rel="noopener noreferrer">@nativeroots_heritage</a>
          </div>
        </div>
        <div className="footer__bar">© {new Date().getFullYear()} Nativeroots · S R Industries · All rights reserved</div>
      </footer>

    </main>
  );
}
